/**
 * Returns the four corner points of a rectangle after rotation around its center.
 * @param {number} x - Top-left x
 * @param {number} y - Top-left y
 * @param {number} width
 * @param {number} height
 * @param {number} rotation - Degrees
 * @returns {Array<{x: number, y: number}>} Four corner points
 */
export function getRotatedCorners(x, y, width, height, rotation) {
  // Center of the rectangle
  const cx = x + width / 2
  const cy = y + height / 2

  // Four corners before rotation (top-left, top-right, bottom-right, bottom-left)
  const corners = [
    { x: x, y: y },
    { x: x + width, y: y },
    { x: x + width, y: y + height },
    { x: x, y: y + height }
  ]

  // If no rotation, return corners as-is
  if (rotation === 0) {
    return corners
  }

  // Convert rotation from degrees to radians
  const rad = (rotation * Math.PI) / 180

  const cosA = Math.cos(rad)
  const sinA = Math.sin(rad)

  // Rotate each corner around the center point
  return corners.map(corner => {
    const dx = corner.x - cx
    const dy = corner.y - cy
    return {
      x: cx + dx * cosA - dy * sinA,
      y: cy + dx * sinA + dy * cosA
    }
  })
}

/**
 * Computes the axis-aligned bounding box enclosing all items.
 * Accounts for rotation by computing rotated corner positions.
 * @param {Array} items - Items with x, y, width, height, rotation
 * @returns {{ minX: number, minY: number, maxX: number, maxY: number, width: number, height: number }}
 */
export function computeBoundingBox(items) {
  if (!items || items.length === 0) {
    return { minX: 0, minY: 0, maxX: 0, maxY: 0, width: 0, height: 0 }
  }

  let minX = Infinity
  let minY = Infinity
  let maxX = -Infinity
  let maxY = -Infinity

  for (const item of items) {
    const rotation = item.rotation || 0
    const corners = getRotatedCorners(item.x, item.y, item.width, item.height, rotation)

    for (const corner of corners) {
      if (corner.x < minX) minX = corner.x
      if (corner.y < minY) minY = corner.y
      if (corner.x > maxX) maxX = corner.x
      if (corner.y > maxY) maxY = corner.y
    }
  }

  return {
    minX,
    minY,
    maxX,
    maxY,
    width: maxX - minX,
    height: maxY - minY
  }
}
